'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import { ScrollText } from 'lucide-react';
import type { ExtendedColumnSort } from '@/types/data-table';
import { getSortingStateParser } from '@/lib/parsers';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { cn } from '@/lib/utils';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { userKeys, usersApi, type User } from '@/features/users';
import { systemLogQueries } from '../services/system-logs.queries';
import { useSystemLogsColumns } from './system-logs-columns';
import { SystemLogsTableActionBar } from './system-logs-table-action-bar';
import { SystemLogCard } from './system-log-card';
import { SystemLogCompactRow } from './system-log-compact-row';
import type { AuditLog, SystemLogsListParams } from '../types';

export function SystemLogsListing() {
  const t = useTranslations('systemLogs');
  const columns = useSystemLogsColumns();
  const [view, setView] = useDataView('system-logs');

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlUser] = useQueryState(
    'user',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<AuditLog>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<AuditLog>[])
  );
  const [urlAction] = useQueryState(
    'action',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSubjectType] = useQueryState(
    'subject_type',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlCreatedAt] = useQueryState(
    'created_at',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );

  const userInfinite = useInfiniteSelect<User>({
    queryKey: userKeys.lists(),
    queryFn: (params) => usersApi.getAll(params),
    mapOption: useCallback(
      (u: User) => ({ value: String(u.id), label: u.name }),
      []
    )
  });

  const queryParams = useMemo<SystemLogsListParams>(() => {
    const params: SystemLogsListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    params.sort_by =
      (urlSorting[0]?.id as SystemLogsListParams['sort_by']) ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';

    if (urlAction.length) params.action = urlAction;
    if (urlSubjectType.length) params.subject_type = urlSubjectType;

    if (urlUser.length) {
      const userIds = urlUser
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n));
      if (userIds.length) params.user_id = userIds;
    }

    if (urlCreatedAt.length) {
      const fromTs = Number(urlCreatedAt[0]);
      const toTs = Number(urlCreatedAt[1]);
      if (Number.isFinite(fromTs) && fromTs > 0) {
        params.date_from = new Date(fromTs).toISOString();
      }
      if (Number.isFinite(toTs) && toTs > 0) {
        params.date_to = new Date(toTs).toISOString();
      }
    }

    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSorting,
    urlAction,
    urlSubjectType,
    urlUser,
    urlCreatedAt
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    systemLogQueries.list(queryParams)
  );

  const logs = (data?.data ?? []) as AuditLog[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<AuditLog>({
    columns: filteredColumns,
    pageKey: 'system-logs',
    data: logs,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('action'), value: 'action' },
      { label: t('subjectType'), value: 'subject_type' },
      { label: t('timestamp'), value: 'created_at' }
    ],
    [t]
  );

  return (
    <div className='space-y-6'>
      {/* Toolbar: View Toggle + Sort */}
      <div className='flex items-center justify-end gap-2'>
        {view !== 'table' && (
          <SortControl
            options={sortOptions}
            sorting={tableState.sorting}
            onSortingChange={(s) => table.setSorting(s)}
          />
        )}
        <DataViewToggle
          value={view}
          onValueChange={setView}
          pageKey='system-logs'
        />
      </div>

      <DataTableToolbar
        table={table}
        infiniteConfigs={{ user: userInfinite }}
        viewFilterPermission='system_logs.view_filter'
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : logs.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <ScrollText className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noLogs')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {logs.map((log) => (
              <SystemLogCard key={log.id} log={log} />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {logs.map((log) => (
              <SystemLogCompactRow key={log.id} log={log} />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <DataTable
            table={table}
            actionBar={<SystemLogsTableActionBar table={table} />}
          />
        </div>
      )}
    </div>
  );
}
